<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router'
import { computed } from 'vue'

// Router for navigation
const router = useRouter()
const route = useRoute()

// Props for customization
interface Props {
  activeItem?: string
}

const props = withDefaults(defineProps<Props>(), {
  activeItem: 'projects'
})

// Navigation menu items
const navigationItems = [
  {
    id: 'home',
    label: 'Home',
    icon: 'fa-solid fa-home',
    route: '/',
    external: true // Goes to main landing page
  },
  {
    id: 'projects',
    label: 'Projects',
    icon: 'fa-solid fa-folder',
    route: '/member/projects'
  },
  {
    id: 'transactions',
    label: 'Transactions',
    icon: 'fa-solid fa-exchange-alt',
    route: '/member/transactions'
  },
  {
    id: 'claims',
    label: 'Claims',
    icon: 'fa-solid fa-clipboard-check',
    route: '/member/claims'
  },
  {
    id: 'methodologies',
    label: 'Methodologies',
    icon: 'fa-solid fa-book',
    route: '/member/methodologies'
  }
]

// Action items (create/new actions)
const actionItems = [
  {
    id: 'new-project',
    label: 'New Project',
    icon: 'fa-solid fa-plus',
    action: 'createProject'
  },
  {
    id: 'new-methodology',
    label: 'New Methodology',
    icon: 'fa-solid fa-plus',
    action: 'createMethodology'
  }
]

// Get active item from current route or props
const currentActiveItem = computed(() => {
  // Auto-detect from route if not explicitly set
  if (route.path === '/member/projects') return 'projects'
  if (route.path === '/member/transactions') return 'transactions'
  if (route.path === '/member/claims') return 'claims'
  if (route.path === '/member/methodologies') return 'methodologies'
  return props.activeItem
})

// Navigation handlers
const navigateToItem = (item: typeof navigationItems[0]) => {
  if (item.external || item.route === '/') {
    // For home or external routes, use push
    router.push(item.route)
  } else {
    // For internal member routes
    router.push(item.route)
  }
}

// Action handlers - emit events for parent components to handle
const emit = defineEmits<{
  action: [actionType: string]
}>()

const handleAction = (actionType: string) => {
  emit('action', actionType)
}

// Helper function to get item classes
const getItemClasses = (itemId: string) => {
  const isActive = currentActiveItem.value === itemId
  return isActive
    ? 'flex items-center px-3 py-2 text-blue-600 bg-blue-50 rounded-lg font-medium cursor-pointer'
    : 'flex items-center px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer'
}

const getIconClasses = (itemId: string) => {
  const isActive = currentActiveItem.value === itemId
  return isActive
    ? 'mr-3 text-blue-600'
    : 'mr-3 text-gray-400'
}
</script>

<template>
  <aside class="w-64 bg-white shadow-sm border-r">
    <nav class="mt-8">
      <div class="px-4">
        <!-- Main Navigation Items -->
        <ul class="space-y-2">
          <li v-for="item in navigationItems" :key="item.id">
            <span 
              @click="navigateToItem(item)"
              :class="getItemClasses(item.id)"
            >
              <i :class="[item.icon, getIconClasses(item.id)]"></i>
              {{ item.label }}
            </span>
          </li>
        </ul>
        
        <!-- Action Items (Create/New) -->
        <div class="border-t mt-6 pt-6">
          <ul class="space-y-2">
            <li v-for="action in actionItems" :key="action.id">
              <span 
                @click="handleAction(action.action)"
                class="flex items-center px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i :class="[action.icon, 'mr-3 text-gray-400']"></i>
                {{ action.label }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </aside>
</template>

<style scoped>
/* Component-specific styles if needed */
</style>